'use client';

import Link from 'next/link';
import { useState } from 'react';

import { SocialAuthButtons } from '@/components/SocialAuthButtons';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { authClient } from '@/lib/auth-client';

export default function SignUpComponent() {
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setIsLoading(true);

    const result = await authClient.signUp.email({
      email,
      password,
      name,
    });

    setIsLoading(false);

    if (result.error) {
      setError(result.error.message ?? 'Sign up failed. Please try again.');
      return;
    }

    setIsSuccess(true);
  }

  if (isSuccess) {
    return (
      <div className="w-full max-w-sm">
        <div className="rounded-lg bg-gray-800 p-8 text-center">
          <h2 className="mb-4 text-xl font-semibold text-gray-100">Check your email</h2>
          <p className="mb-4 text-sm text-gray-400">
            We sent a verification link to <span className="font-medium text-gray-200">{email}</span>. Please check your
            inbox and click the link to verify your account.
          </p>
          <Link href="/sign-in" className="text-sm text-primary-500 hover:text-primary-400">
            Back to sign in
          </Link>
        </div>
      </div>
    );
  }

  return (
    <div className="w-full max-w-sm">
      <div className="rounded-lg bg-gray-800 p-8">
        <h2 className="mb-6 text-center text-xl font-semibold text-gray-100">Create your account</h2>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="name" className="text-gray-300">
              Name
            </Label>
            <Input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Your name"
              required
              className="border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-gray-300">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              required
              className="border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-500"
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password" className="text-gray-300">
              Password
            </Label>
            <Input
              id="password"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="At least 8 characters"
              required
              minLength={8}
              className="border-gray-600 bg-gray-700 text-gray-200 placeholder:text-gray-500"
            />
          </div>

          {error && <p className="text-sm text-red-400">{error}</p>}

          <Button type="submit" disabled={isLoading} className="w-full bg-primary-700 hover:bg-primary-600">
            {isLoading ? 'Creating account...' : 'Sign Up'}
          </Button>
        </form>

        <div className="mt-4 text-center text-sm">
          <p className="text-gray-500">
            Already have an account?{' '}
            <Link href="/sign-in" className="text-gray-300 hover:text-primary-500">
              Sign in
            </Link>
          </p>
        </div>

        <div className="relative my-6">
          <div className="absolute inset-0 flex items-center">
            <div className="w-full border-t border-gray-600" />
          </div>
          <div className="relative flex justify-center text-xs">
            <span className="bg-gray-800 px-2 text-gray-500">or</span>
          </div>
        </div>

        <SocialAuthButtons />
      </div>
    </div>
  );
}
